import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './HandicapAdjustmentManager.css';
import BottomNav from './BottomNav';
import {
  FiBarChart2, FiGrid, FiSettings,
  FiAlertCircle, FiInfo,
  FiTrash2, FiZap
} from 'react-icons/fi';
import { GiGolfTee } from 'react-icons/gi';
import { useApi } from './useApi';
import { useAuth } from './AuthContext';
import { Permission } from './permissions';
import PageHeader from './components/PageHeader';

const ordinalOf = (n) => {
  if (n === 1) return '1st';
  if (n === 2) return '2nd';
  if (n === 3) return '3rd';
  return `${n}th`;
};

/**
 * One rule row, used by BOTH the Score and Position tabs so a rule is removed
 * the same way whichever tab you are on.
 *
 * Removing a rule is destructive and silent — a deleted range stops adjusting
 * handicaps at the next finalisation and there is no undo — so the row asks
 * first, in place, naming what is about to go. The trash button is a full
 * 44px target rather than a 16px glyph in a 4px box, and everything is disabled
 * while the request is in flight so a double tap cannot fire it twice.
 */
const RuleRow = ({ label, value, valueClass, describe, confirming, busy, onAskDelete, onCancelDelete, onConfirmDelete }) => {
  if (confirming) {
    return (
      <div className="ios-row ios-row--confirm">
        <span className="row-confirm-label">Remove {describe}?</span>
        <div className="row-right">
          <button className="row-confirm-cancel" onClick={onCancelDelete} disabled={busy}>Cancel</button>
          <button className="row-confirm-delete" onClick={onConfirmDelete} disabled={busy}>
            {busy ? 'Removing…' : 'Remove'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ios-row">
      <span className="row-label">{label}</span>
      <div className="row-right">
        <span className={`row-value ${valueClass}`}>{value}</span>
        <button
          className="ios-delete"
          onClick={onAskDelete}
          disabled={busy}
          aria-label={`Remove ${describe}`}
          title={`Remove ${describe}`}
        >
          <FiTrash2 size={18} />
        </button>
      </div>
    </div>
  );
};

const HandicapAdjustmentManager = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { authenticatedFetch } = useApi();
  
  // -- State --
  const [adjustments, setAdjustments] = useState([]);
  const [, setSaving] = useState(false);
  
  // UI State
  // Tabs are navigation, not a locked wizard — any tab, any time.
  const [activeTab, setActiveTab] = useState('score');
  
  // Forms
  const [segmentForm, setSegmentForm] = useState({ start: '', end: '', adjustment: '' });
  // `fromLast` writes a negative value: -1 is last place, -2 one above it.
  const [positionForm, setPositionForm] = useState({ position: '', adjustment: '', fromLast: false });
  const [showSegmentForm, setShowSegmentForm] = useState(false);
  const [showPositionForm, setShowPositionForm] = useState(false);

  // Deletion: which row is asking to confirm, which is mid-request, and what
  // to say if the server refused. Only ever one of each — a rule at a time.
  const [confirmKey, setConfirmKey] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [actionError, setActionError] = useState(null);

  // Preview State
  const [previewScore, setPreviewScore] = useState(36);
  const [previewPos, setPreviewPos] = useState(1);
  const [animateTotal, setAnimateTotal] = useState(false);

  // -- Data Fetching --
  // Sequenced: a refetch fired after a delete must not be overwritten by an
  // older in-flight one, or a rule that has genuinely gone reappears in the
  // list and the delete looks like it failed.
  const fetchSeq = useRef(0);
  const fetchAdjustments = useCallback(async () => {
    const seq = ++fetchSeq.current;
    try {
      const res = await authenticatedFetch('/handicap_adjustments');
      if (res.ok) {
        const data = await res.json();
        if (seq === fetchSeq.current) setAdjustments(data);
      }
    } catch (e) {
      console.error(e);
    }
  }, [authenticatedFetch]);

  useEffect(() => {
    if (hasPermission(Permission.MANAGE_HANDICAPS)) {
      fetchAdjustments();
    }
  }, [fetchAdjustments, hasPermission]);

  // -- Derived State (Segments) --
  const scoreAdjustments = useMemo(() => 
    adjustments.filter(a => a.adjustment_type === 'SCORE').sort((a,b) => a.value - b.value),
  [adjustments]);

  const positionAdjustments = useMemo(() =>
    adjustments
      .filter(a => a.adjustment_type === 'POSITION')
      .sort((a, b) => {
        // 1st, 2nd, 3rd … then "2nd from last", then "last".
        if ((a.value < 0) !== (b.value < 0)) return a.value < 0 ? 1 : -1;
        return a.value < 0 ? a.value - b.value : a.value - b.value;
      }),
  [adjustments]);

  const previewData = useMemo(() => {
    const scoreRule = scoreAdjustments.find(a => a.value === parseInt(previewScore));
    const posRule = positionAdjustments.find(a => a.value === parseInt(previewPos));
    const scoreAdj = scoreRule ? scoreRule.adjustment : 0;
    const posAdj = posRule ? posRule.adjustment : 0;
    return { scoreAdj, posAdj, total: scoreAdj + posAdj };
  }, [previewScore, previewPos, scoreAdjustments, positionAdjustments]);

  useEffect(() => {
    setAnimateTotal(true);
    const timer = setTimeout(() => setAnimateTotal(false), 200);
    return () => clearTimeout(timer);
  }, [previewData.total]);

  const scoreSegments = useMemo(() => {
    if (scoreAdjustments.length === 0) return [];
    const segments = [];
    let current = {
      start: scoreAdjustments[0].value,
      end: scoreAdjustments[0].value,
      adjustment: scoreAdjustments[0].adjustment,
      ids: [scoreAdjustments[0].id]
    };

    for (let i = 1; i < scoreAdjustments.length; i++) {
      const adj = scoreAdjustments[i];
      const prev = scoreAdjustments[i-1];
      if (adj.value === prev.value + 1 && adj.adjustment === prev.adjustment) {
        current.end = adj.value;
        current.ids.push(adj.id);
      } else {
        segments.push(current);
        current = {
          start: adj.value,
          end: adj.value,
          adjustment: adj.adjustment,
          ids: [adj.id]
        };
      }
    }
    segments.push(current);
    return segments;
  }, [scoreAdjustments]);

  const handleSaveSegment = async () => {
    const start = parseInt(segmentForm.start);
    const end = parseInt(segmentForm.end);
    const adjVal = parseFloat(segmentForm.adjustment);
    if (isNaN(start) || isNaN(end) || isNaN(adjVal)) return;
    setSaving(true);
    const promises = [];
    for (let v = start; v <= end; v++) {
      const existing = scoreAdjustments.find(a => a.value === v);
      if (existing) {
        if (existing.adjustment !== adjVal) {
            promises.push(authenticatedFetch(`/handicap_adjustments/${existing.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ adjustment: adjVal })
            }));
        }
      } else {
        promises.push(authenticatedFetch('/handicap_adjustments', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                adjustment_type: 'SCORE',
                value: v,
                adjustment: adjVal
            })
        }));
      }
    }
    setActionError(null);
    try {
      await Promise.all(promises);
      setSegmentForm({ start: '', end: '', adjustment: '' });
      setShowSegmentForm(false);
      fetchAdjustments();
    } catch (e) {
      // Without this the rejection was silent and the form just sat there.
      setActionError('Could not reach the server, so the range was not saved. Check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  // A conventional society ladder, offered when there are no score rules yet.
  // Nothing here is special-cased downstream — it just writes the same rows the
  // Add-range form does, so every value stays editable afterwards.
  const STARTER_SEGMENTS = [
    { start: 20, end: 29, adjustment: 0.5, blurb: 'a tough day — half a shot back' },
    { start: 30, end: 35, adjustment: 0, blurb: 'about par for the course — no change' },
    { start: 36, end: 38, adjustment: -0.5, blurb: 'a good round — small cut' },
    { start: 39, end: 41, adjustment: -1, blurb: 'a very good round' },
    { start: 42, end: 50, adjustment: -2, blurb: 'exceptional — a full two shots' },
  ];

  const applyStarterSet = async () => {
    if (!window.confirm('Add a starter set of five score rules? You can edit or delete any of them afterwards.')) return;
    setSaving(true);
    try {
      const posts = [];
      STARTER_SEGMENTS.forEach(({ start, end, adjustment }) => {
        for (let v = start; v <= end; v++) {
          if (scoreAdjustments.find((a) => a.value === v)) continue;
          posts.push(authenticatedFetch('/handicap_adjustments', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ adjustment_type: 'SCORE', value: v, adjustment }),
          }));
        }
      });
      await Promise.all(posts);
      fetchAdjustments();
    } catch (e) {
      setActionError('Could not reach the server, so the starter set was not added. Check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Remove one or more stored rules. A score range is one row per point, so
   * this is often several ids — they go in a single request that either
   * removes them all or removes none, because a half-deleted range still
   * adjusts handicaps at the next finalisation while looking gone-ish here.
   * A failure is SAID OUT LOUD; the old code swallowed it, which is why a
   * delete could appear to do nothing.
   */
  const removeRules = async (key, ids) => {
    if (busyKey) return;
    setBusyKey(key);
    setActionError(null);
    try {
      const res = await authenticatedFetch('/handicap_adjustments/bulk_delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) {
        setActionError(res.status === 403
          ? 'You no longer have permission to change these rules. Nothing was removed.'
          : 'That rule could not be removed. Nothing was changed — please try again.');
        return;
      }
      // Drop it locally first so the list is right immediately, then reconcile.
      setAdjustments(prev => prev.filter(a => !ids.includes(a.id)));
      // Only close OUR prompt — the user may have opened another row's since.
      setConfirmKey(prev => (prev === key ? null : prev));
      fetchAdjustments();
    } catch (e) {
      setActionError('Could not reach the server, so nothing was removed. Check your connection and try again.');
    } finally {
      setBusyKey(null);
    }
  };

  const askDelete = (key) => {
    setActionError(null);
    setConfirmKey(key);
  };

  // A negative stored value means "from the bottom": -1 last, -2 one above.
  const positionLabel = (v) => {
    if (v === -1) return 'Last place';
    if (v < 0) return `${ordinalOf(Math.abs(v))} from last`;
    return ordinalOf(v);
  };

  const handleSavePosition = async () => {
      const entered = parseInt(positionForm.position);
      const adjVal = parseFloat(positionForm.adjustment);
      if (isNaN(entered) || isNaN(adjVal) || entered < 1) return;
      const pos = positionForm.fromLast ? -entered : entered;
      setSaving(true);
      setActionError(null);
      const existing = positionAdjustments.find(a => a.value === pos);
      try {
          let res;
          if (existing) {
              res = await authenticatedFetch(`/handicap_adjustments/${existing.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ adjustment: adjVal })
              });
          } else {
              res = await authenticatedFetch('/handicap_adjustments', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    adjustment_type: 'POSITION',
                    value: pos,
                    adjustment: adjVal
                })
              });
          }
          if (res.ok) {
              setPositionForm({ position: '', adjustment: '', fromLast: false });
              setShowPositionForm(false);
              fetchAdjustments();
          } else {
              setActionError('That position rule could not be saved. Please try again.');
          }
      } catch (e) {
          setActionError('Could not reach the server, so the rule was not saved. Check your connection and try again.');
      } finally {
          setSaving(false);
      }
  };

  const formatAdj = (val) => {
      if (val > 0) return `+${val.toFixed(1)}`;
      if (val === 0) return "0.0";
      return `−${Math.abs(val).toFixed(1)}`;
  };

  if (!hasPermission(Permission.MANAGE_HANDICAPS)) {
      return <AccessDenied navigate={navigate} />;
  }

  const MAX_VAL = 4;
  const MIN_VAL = -6;
  const RANGE = MAX_VAL - MIN_VAL;

  // Which rule actually fired for the scenario, so the Test tab can name it
  // rather than showing a bare 0.0 for "nothing matched".
  const firedScoreSegment = scoreSegments.find(
    (seg) => Number(previewScore) >= seg.start && Number(previewScore) <= seg.end
  );
  const firedPositionRule = positionAdjustments.find((a) => a.value === parseInt(previewPos));

  const TABS = [
    { key: 'score', label: 'Score', count: scoreSegments.length },
    { key: 'position', label: 'Position', count: positionAdjustments.length },
    { key: 'test', label: 'Test', count: null },
  ];

  const scenarioChip = (
    <div className="input-row">
      <select value={previewScore} onChange={e => setPreviewScore(e.target.value)} className="inline-select">
        {Array.from({length: 31}, (_, i) => i + 20).map(s => <option key={s} value={s}>{s} pts</option>)}
      </select>
      <span className="bullet">•</span>
      <select value={previewPos} onChange={e => setPreviewPos(e.target.value)} className="inline-select">
        {Array.from({length: 20}, (_, i) => i + 1).map(p => (
          <option key={p} value={p}>{ordinalOf(p)} Place</option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="handicap-adjustment-manager">
      <PageHeader title="Handicap System" backTo="/setup" backLabel="Back to Manage" />

      <div className="ios-container">

        {/* One instruction for the whole screen: why the rules exist, and how
            the tabs work together. Stays put as you move between them. */}
        <p className="info-note">
          <FiInfo size={15} />
          <span>
            Keep competitions close: cut the good rounds, give a little back to the rest.
            Set your rules in <strong>Score</strong> and <strong>Position</strong>,
            then <strong>Test</strong> them.
          </span>
        </p>

        <div className="hcp-tabs" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key}
              role="tab"
              aria-selected={activeTab === t.key}
              className={`hcp-tab${activeTab === t.key ? ' hcp-tab--active' : ''}`}
              onClick={() => {
                // Leaving a tab abandons whatever it was asking about.
                setConfirmKey(null);
                setActionError(null);
                setActiveTab(t.key);
              }}
            >
              {t.label}
              {t.count > 0 && <span className="hcp-tab-count">{t.count}</span>}
            </button>
          ))}
        </div>

        {/* ── Score ─────────────────────────────────────────────────────── */}
        {activeTab === 'score' && (
          <div className="hcp-panel">
            {scoreSegments.length > 0 && (
              <div className="premium-chart-container">
                <div className="chart-area">
                  {Array.from({length: 31}, (_, i) => i + 20).map(pt => {
                    const rule = scoreAdjustments.find(r => r.value === pt);
                    const val = rule ? rule.adjustment : 0;
                    const clampedVal = Math.max(MIN_VAL, Math.min(MAX_VAL, val));
                    const heightPct = (Math.abs(clampedVal) / RANGE) * 100;
                    return (
                      <div key={pt} className="chart-bar-wrapper">
                        <div
                          className={`chart-bar-minimal ${val < 0 ? 'bar-cut' : val > 0 ? 'bar-add' : ''}`}
                          style={{
                            height: `${heightPct}%`,
                            bottom: val >= 0 ? '50%' : 'auto',
                            top: val < 0 ? '50%' : 'auto'
                          }}
                        ></div>
                        {(pt % 5 === 0 && pt !== 35) || pt === 36 ? <span className="chart-pt-label">{pt}</span> : null}
                      </div>
                    );
                  })}
                  <div className="neutral-line-minimal"></div>
                </div>
              </div>
            )}

            {scoreSegments.length === 0 && !showSegmentForm && (
              <div className="rule-empty">
                <p className="rule-empty-text">
                  No score rules yet, so a round's points change nothing. Start from a
                  typical society ladder and tune it, or add your own range.
                </p>
                <button className="rule-starter-btn" onClick={applyStarterSet}>
                  <FiZap size={15} /> Use a starter set
                </button>
                <ul className="rule-starter-list">
                  {STARTER_SEGMENTS.map((s) => (
                    <li key={s.start}>
                      <strong>{s.start}–{s.end} pts</strong> {formatAdj(s.adjustment)}
                      <span> — {s.blurb}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="ios-list">
              {scoreSegments.map((seg) => {
                const key = `score-${seg.ids.join('-')}`;
                const range = seg.start === seg.end ? `${seg.start} pts` : `${seg.start}–${seg.end} pts`;
                return (
                  <RuleRow
                    key={key}
                    label={range}
                    value={formatAdj(seg.adjustment)}
                    valueClass={seg.adjustment < 0 ? 'text-cut' : seg.adjustment > 0 ? 'text-add' : ''}
                    describe={`the ${range} rule`}
                    confirming={confirmKey === key}
                    busy={busyKey === key}
                    onAskDelete={() => askDelete(key)}
                    onCancelDelete={() => setConfirmKey(null)}
                    onConfirmDelete={() => removeRules(key, seg.ids)}
                  />
                );
              })}
              {actionError && (
                <p className="rule-error" role="alert">
                  <FiAlertCircle size={15} /> <span>{actionError}</span>
                </p>
              )}
              {!showSegmentForm ? (
                <button className="ios-add-button" onClick={() => setShowSegmentForm(true)}>+ Add range</button>
              ) : (
                <div className="inline-edit-form">
                  <p className="form-hint">
                    A points range, and what it does to the handicap.
                    Use a <strong>minus</strong> to cut, a <strong>plus</strong> to give shots back.
                  </p>
                  <div className="input-group">
                    <label className="field-label">From<input type="number" placeholder="36" value={segmentForm.start} onChange={e => setSegmentForm({...segmentForm, start: e.target.value})} /></label>
                    <label className="field-label">To<input type="number" placeholder="38" value={segmentForm.end} onChange={e => setSegmentForm({...segmentForm, end: e.target.value})} /></label>
                    <label className="field-label">Change<input type="number" step="0.1" placeholder="-0.5" value={segmentForm.adjustment} onChange={e => setSegmentForm({...segmentForm, adjustment: e.target.value})} /></label>
                  </div>
                  {segmentForm.start !== '' && segmentForm.adjustment !== '' && (
                    <p className="form-preview">
                      {segmentForm.start}{segmentForm.end !== '' && segmentForm.end !== segmentForm.start ? `–${segmentForm.end}` : ''} pts
                      {' → '}
                      {Number(segmentForm.adjustment) < 0 ? `cut ${Math.abs(Number(segmentForm.adjustment)).toFixed(1)}`
                        : Number(segmentForm.adjustment) > 0 ? `add ${Number(segmentForm.adjustment).toFixed(1)}`
                        : 'no change'}
                    </p>
                  )}
                  <div className="form-actions">
                    <button onClick={() => setShowSegmentForm(false)}>Cancel</button>
                    <button className="save" onClick={handleSaveSegment}>Save</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Position ──────────────────────────────────────────────────── */}
        {activeTab === 'position' && (
          <div className="hcp-panel">
            {positionAdjustments.length === 0 && !showPositionForm && (
              <p className="rule-empty-text rule-empty-text--solo">
                No position rules yet. Cut the winner on top of whatever their score
                already earned, or give the back marker a shot back — pick
                <strong> From the bottom</strong> for last place, however many played.
              </p>
            )}

            <div className="ios-list">
              {positionAdjustments.map(adj => {
                const key = `pos-${adj.id}`;
                const medal = adj.value === 1 ? '🥇 1st' : adj.value === 2 ? '🥈 2nd' : adj.value === 3 ? '🥉 3rd' : null;
                const posText = adj.value > 0
                  ? `${ordinalOf(adj.value)} place`
                  : positionLabel(adj.value).toLowerCase();
                return (
                  <RuleRow
                    key={key}
                    label={medal || positionLabel(adj.value)}
                    value={formatAdj(adj.adjustment)}
                    valueClass={adj.adjustment < 0 ? 'text-cut' : adj.adjustment > 0 ? 'text-add' : ''}
                    describe={`the ${posText} rule`}
                    confirming={confirmKey === key}
                    busy={busyKey === key}
                    onAskDelete={() => askDelete(key)}
                    onCancelDelete={() => setConfirmKey(null)}
                    onConfirmDelete={() => removeRules(key, [adj.id])}
                  />
                );
              })}
              {actionError && (
                <p className="rule-error" role="alert">
                  <FiAlertCircle size={15} /> <span>{actionError}</span>
                </p>
              )}
              {!showPositionForm ? (
                <button className="ios-add-button" onClick={() => setShowPositionForm(true)}>+ Add position rule</button>
              ) : (
                <div className="inline-edit-form">
                  <p className="form-hint">
                    A finishing position, and what it does to the handicap.
                    Use a <strong>minus</strong> to cut, a <strong>plus</strong> to give shots back.
                  </p>
                  <div className="pos-mode-toggle">
                    <button
                      className={`pos-mode${!positionForm.fromLast ? ' pos-mode--active' : ''}`}
                      onClick={() => setPositionForm({ ...positionForm, fromLast: false })}
                    >From the top</button>
                    <button
                      className={`pos-mode${positionForm.fromLast ? ' pos-mode--active' : ''}`}
                      onClick={() => setPositionForm({ ...positionForm, fromLast: true, position: positionForm.position || '1' })}
                    >From the bottom</button>
                  </div>
                  <div className="input-group">
                    <label className="field-label">
                      {positionForm.fromLast ? 'Place from last' : 'Position'}
                      <input type="number" min="1" placeholder="1" value={positionForm.position} onChange={e => setPositionForm({...positionForm, position: e.target.value})} />
                    </label>
                    <label className="field-label">Change<input type="number" step="0.1" placeholder="+0.5" value={positionForm.adjustment} onChange={e => setPositionForm({...positionForm, adjustment: e.target.value})} /></label>
                  </div>
                  {positionForm.position !== '' && positionForm.adjustment !== '' && (
                    <p className="form-preview">
                      Finishing {positionLabel(positionForm.fromLast ? -Math.abs(parseInt(positionForm.position) || 1) : (parseInt(positionForm.position) || 1)).toLowerCase()}
                      {' → '}
                      {Number(positionForm.adjustment) < 0 ? `cut ${Math.abs(Number(positionForm.adjustment)).toFixed(1)}`
                        : Number(positionForm.adjustment) > 0 ? `add ${Number(positionForm.adjustment).toFixed(1)}`
                        : 'no change'}
                    </p>
                  )}
                  <div className="form-actions">
                    <button onClick={() => setShowPositionForm(false)}>Cancel</button>
                    <button className="save" onClick={handleSavePosition}>Save</button>
                  </div>
                </div>
              )}
            </div>

            {/* Live strip: the combined effect on the scenario shared with the
                Test tab, so a rule's real-world impact is visible while editing. */}
            <div className="live-preview">
              <span className="live-preview-label">
                Preview · {previewScore} pts, {ordinalOf(parseInt(previewPos))}
              </span>
              <span className={`live-preview-value ${previewData.total < 0 ? 'text-cut' : previewData.total > 0 ? 'text-add' : 'text-neutral'}`}>
                {formatAdj(previewData.total)}
              </span>
            </div>
          </div>
        )}

        {/* ── Test ──────────────────────────────────────────────────────── */}
        {activeTab === 'test' && (
          <div className="hcp-panel">
            <div className="hero-result-card">
              <span className={`hero-value ${animateTotal ? 'pop' : ''} ${previewData.total < 0 ? 'text-cut' : previewData.total > 0 ? 'text-add' : 'text-neutral'}`}>
                {formatAdj(previewData.total)}
              </span>
              <span className="hero-label">Total handicap change</span>
              <div className="hero-divider"></div>
              <div className="hero-subtext">
                <span className="hero-try">Try a result</span>
                {scenarioChip}
              </div>
            </div>

            <div className="calculation-summary">
              <h3 className="summary-title">Which rules fired</h3>
              <div className="summary-row">
                <span className="label">
                  Score rule
                  {firedScoreSegment && <span className="rule-ref"> · {firedScoreSegment.start === firedScoreSegment.end
                    ? `${firedScoreSegment.start} pts`
                    : `${firedScoreSegment.start}–${firedScoreSegment.end} pts`}</span>}
                </span>
                {firedScoreSegment
                  ? <span className="value">{formatAdj(previewData.scoreAdj)}</span>
                  : <span className="value value--none">no rule set</span>}
              </div>
              <div className="summary-row">
                <span className="label">
                  Position rule
                  {firedPositionRule && <span className="rule-ref"> · {positionLabel(firedPositionRule.value).toLowerCase()}</span>}
                </span>
                {firedPositionRule
                  ? <span className="value">{formatAdj(previewData.posAdj)}</span>
                  : <span className="value value--none">no rule set</span>}
              </div>
            </div>
          </div>
        )}

      </div>

      <BottomNav menuItems={[
          { name: 'Leaderboard', route: '/leaderboard', icon: <FiBarChart2 size={24} /> },
          { name: 'Rounds', route: '/tournaments/scorecard-entry', icon: <FiGrid size={24} /> },
          { name: 'Play Golf', route: '/play', icon: <GiGolfTee size={24} /> },
          { name: 'Manage', route: '/setup', icon: <FiSettings size={24} />, active: true },
        ]} />
    </div>
  );
};

const AccessDenied = ({ navigate }) => (
    <div className="handicap-adjustment-manager">
        <div style={{ padding: '40px', textAlign: 'center', color: '#555' }}>
          <FiAlertCircle size={48} color="#d9534f" style={{ marginBottom: '20px' }} />
          <h3>Permission Required</h3>
          <p>You do not have permission to manage handicap adjustments.</p>
          <button onClick={() => navigate('/setup')} className="btn-primary" style={{ marginTop: '20px' }}>Back to Setup</button>
        </div>
        <BottomNav menuItems={[
          { name: 'Leaderboard', route: '/leaderboard', icon: <FiBarChart2 size={24} /> },
          { name: 'Rounds', route: '/tournaments/scorecard-entry', icon: <FiGrid size={24} /> },
          { name: 'Play Golf', route: '/play', icon: <GiGolfTee size={24} /> },
          { name: 'Manage', route: '/setup', icon: <FiSettings size={24} />, active: true },
        ]} />
    </div>
);

export default HandicapAdjustmentManager;